import React, { useEffect, useState } from 'react';
import { Card, Typography, Empty, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { useSelector } from 'react-redux';
import tenderDashboardApis from '../../api/tenderDashboard/tenderDashboardApis';

const { Title, Text } = Typography;

type RiskData = {
  lowBids: number;
  overdueEvaluations: number;
  scoreConflicts: number;
  zeroResponses: number;
  multipleRevisions: number;
};

type RiskCategory = {
  key: keyof RiskData;
  label: string;
};

const RISK_CODE_TO_KEY: Record<string, keyof RiskData> = {
  LOW_BIDS: 'lowBids',
  OVERDUE_EVALUATION: 'overdueEvaluations',
  SCORE_CONFLICTS: 'scoreConflicts',
  ZERO_RESPONSES: 'zeroResponses',
  MULTIPLE_REVISIONS: 'multipleRevisions',
};

const EMPTY_RISK: RiskData = {
  lowBids: 0,
  overdueEvaluations: 0,
  scoreConflicts: 0,
  zeroResponses: 0,
  multipleRevisions: 0,
};

const TenderRiskIndicators: React.FC = () => {
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgId = currentUser?.orgid;

  const [data, setData] = useState<RiskData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await tenderDashboardApis.getRiskIndicators({
        orgIds: String(orgId),
      });
      const rows: any[] = res?.data?.data || [];
      const next: RiskData = { ...EMPTY_RISK };
      rows.forEach((r) => {
        const key = RISK_CODE_TO_KEY[r?.riskCode];
        if (key) next[key] = r?.riskCount ?? 0;
      });
      setData(next);
    } catch (e) {
      setData(EMPTY_RISK);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const handleRefresh = () => {
    fetchData();
  };

  const categories: RiskCategory[] = [
    { key: 'lowBids', label: 'Low\nBids (<3)' },
    { key: 'overdueEvaluations', label: 'Overdue\nEvaluations' },
    { key: 'scoreConflicts', label: 'Score\nConflicts (>25%)' },
    { key: 'zeroResponses', label: 'Zero\nResponses' },
    { key: 'multipleRevisions', label: 'Multiple\nScore Revisions' }
  ];

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      textStyle: { color: '#000' }
    },
    grid: { left: '4%', right: '4%', bottom: '10%', top: '12%', containLabel: true },
    xAxis: {
      type: 'category',
      data: categories.map(c => c.label),
      axisLabel: {
        interval: 0,
        fontSize: 12,
        lineHeight: 16,
         color: '#000', fontWeight: 'bold'
      },
      axisLine: { lineStyle: { color: '#ccc' } }
    },
    yAxis: {
      type: 'value',
      name: 'Count',
      axisLine: { lineStyle: { color: '#000' } },
      axisLabel: { fontSize: 12, color: '#000', fontWeight: 'bold' }
    },
    series: [
      {
        type: 'bar',
        data: data ? categories.map(c => data[c.key]) : [],
        itemStyle: {
          color: '#3762e4',
          borderRadius: [4, 4, 0, 0]
        },
        barWidth: 40
      }
    ]
  };

  return (
    <Card

       title={
        <div style={{ padding: '10px 0' }}>
          <Title level={5} style={{ marginBottom: 0, color: "#004293" }}>
            Tender Risk Indicators
          </Title>
          <Text type="secondary" style={{ fontSize: 12, color: '#8094ae' }}>
            Potential issues in the tendering process
          </Text>
        </div>
      }
      extra={<ReloadOutlined onClick={handleRefresh} style={{ cursor: 'pointer' }} />}
      style={{ width: '100%' }}
      bodyStyle={{ padding: '0 12px 12px 12px' }}
    >
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <Spin tip="Loading chart..." />
        </div>
      ) : !data || Object.values(data).every(v => v === 0) ? (
        <Empty description="No risk indicators found" />
      ) : (
        <ReactECharts option={option} style={{ height: 345 }} />
      )}
    </Card>
  );
};

export default TenderRiskIndicators;
